import React, {
	createContext,
	useState,
	useContext,
	ReactNode,
	useCallback,
	useRef,
	useEffect,
} from 'react';
import organizationService, {
	Organization,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
} from '../services/api/organizationService';

interface OrganizationContextType {
	selectedOrganization: Organization | null;
	setSelectedOrganization: (org: Organization | null) => void;
	organizations: Organization[];
	isLoading: boolean;
	error: string | null;

	// API Methods
	fetchOrganizations: () => Promise<Organization[]>;
	fetchOrganizationById: (id: string) => Promise<Organization>;
	createOrganization: (data: CreateOrganizationRequest) => Promise<Organization>;
	updateOrganization: (id: string, data: UpdateOrganizationRequest) => Promise<Organization>;
	uploadLogo: (id: string, file: File) => Promise<{ logoUrl: string }>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [selectedOrganization, setSelectedOrganizationState] = useState<Organization | null>(
		null,
	);
	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	// Use refs to track fetch status without causing re-renders
	const hasAttemptedFetch = useRef<boolean>(false);
	const fetchPromiseRef = useRef<Promise<Organization[]> | null>(null);

	// Emit organization-changed event whenever selectedOrganization changes
	useEffect(() => {
		if (selectedOrganization) {
			window.dispatchEvent(
				new CustomEvent('organization-changed', {
					detail: { organizationId: selectedOrganization.id },
				}),
			);
		}
	}, [selectedOrganization]);

	// Wrapper to set selected organization
	const setSelectedOrganization = useCallback((org: Organization | null) => {
		setSelectedOrganizationState(org);
	}, []);

	// Fetch all organizations
	const fetchOrganizations = useCallback(async (): Promise<Organization[]> => {
		// If we're already fetching, return the existing promise to prevent duplicate calls
		if (fetchPromiseRef.current) {
			return fetchPromiseRef.current;
		}

		// If we've already fetched once, return existing organizations
		if (hasAttemptedFetch.current) {
			return organizations;
		}

		// Create a new fetch promise
		const fetchPromise = (async () => {
			try {
				setIsLoading(true);
				setError(null);
				const data = await organizationService.getOrganizations();
				setOrganizations(data);

				// Set first organization as selected if there is no selected organization yet
				setSelectedOrganizationState((current) => {
					if (!current && data.length > 0) {
						return data[0];
					}
					return current;
				});

				return data;
			} catch (err) {
				console.error('Error fetching organizations:', err);
				setError('Failed to load organizations');
				throw err;
			} finally {
				setIsLoading(false);
				hasAttemptedFetch.current = true;
				fetchPromiseRef.current = null;
			}
		})();

		// Store the promise in the ref
		fetchPromiseRef.current = fetchPromise;
		return fetchPromise;
	}, [organizations]);

	// Auto-fetch organizations on mount - but only for company users and when authenticated
	useEffect(() => {
		const autoFetchOrganizations = async () => {
			try {
				// Only fetch if we haven't attempted before and we're not already loading
				if (!hasAttemptedFetch.current && !isLoading) {
					console.log('Auto-fetching organizations...');
					await fetchOrganizations();
					console.log('Organizations fetched successfully');
				}
			} catch (error: any) {
				console.error('Auto-fetch organizations failed:', error);
				// Don't retry immediately on auth errors
				if (error?.response?.status === 401) {
					console.log(
						'Organization fetch failed due to auth error, will retry when token is refreshed',
					);
				}
			}
		};

		// Fetch organizations on mount
		autoFetchOrganizations();
	}, [fetchOrganizations, isLoading]);

	// Retry organizations fetch when token is refreshed
	useEffect(() => {
		const handleTokenRefreshed = async () => {
			// Reset the fetch attempt flag so we can retry
			if (hasAttemptedFetch.current) {
				console.log('Token refreshed, retrying organization fetch...');
				hasAttemptedFetch.current = false;
				fetchPromiseRef.current = null;
				try {
					await fetchOrganizations();
				} catch (error) {
					console.error('Retry organization fetch after token refresh failed:', error);
				}
			}
		};

		window.addEventListener('auth-token-refreshed', handleTokenRefreshed);
		return () => {
			window.removeEventListener('auth-token-refreshed', handleTokenRefreshed);
		};
	}, [fetchOrganizations]);

	// Fetch organization by ID
	const fetchOrganizationById = useCallback(
		async (id: string): Promise<Organization> => {
			// First check if we already have this organization in state
			const existingOrg = organizations.find((org) => org.id === id);
			if (existingOrg) {
				return existingOrg;
			}

			try {
				setIsLoading(true);
				setError(null);
				const data = await organizationService.getOrganizationById(id);
				return data;
			} catch (err) {
				console.error(`Error fetching organization with ID ${id}:`, err);
				setError(`Failed to load organization details`);
				throw err;
			} finally {
				setIsLoading(false);
			}
		},
		[organizations],
	);

	// Create new organization
	const createOrganization = useCallback(
		async (data: CreateOrganizationRequest): Promise<Organization> => {
			try {
				setIsLoading(true);
				setError(null);
				const newOrg = await organizationService.createOrganization(data);

				// Update organizations state
				setOrganizations((prevOrgs) => [...prevOrgs, newOrg]);

				// Auto-select the newly created organization
				setSelectedOrganizationState(newOrg);

				return newOrg;
			} catch (err) {
				console.error('Error creating organization:', err);
				setError('Failed to create organization');
				throw err;
			} finally {
				setIsLoading(false);
			}
		},
		[],
	);

	// Update organization
	const updateOrganization = useCallback(
		async (id: string, data: UpdateOrganizationRequest): Promise<Organization> => {
			try {
				setIsLoading(true);
				setError(null);
				const updatedOrg = await organizationService.updateOrganization(id, data);

				// Update organizations state
				setOrganizations((prevOrgs) =>
					prevOrgs.map((org) => (org.id === id ? updatedOrg : org)),
				);

				// Update selected organization if this was the selected one
				if (selectedOrganization && selectedOrganization.id === id) {
					setSelectedOrganization(updatedOrg);
				}

				return updatedOrg;
			} catch (err) {
				console.error(`Error updating organization with ID ${id}:`, err);
				setError('Failed to update organization');
				throw err;
			} finally {
				setIsLoading(false);
			}
		},
		[selectedOrganization],
	);

	// Upload organization logo
	const uploadLogo = useCallback(
		async (id: string, file: File): Promise<{ logoUrl: string }> => {
			try {
				const result = await organizationService.uploadLogo(id, file);

				// Update organizations state with new logo URL
				setOrganizations((prevOrgs) =>
					prevOrgs.map((org) =>
						org.id === id ? { ...org, logoUrl: result.logoUrl } : org,
					),
				);

				// Update selected organization if this was the selected one
				if (selectedOrganization && selectedOrganization.id === id) {
					setSelectedOrganization({ ...selectedOrganization, logoUrl: result.logoUrl });
				}

				return result;
			} catch (err) {
				console.error(`Error uploading logo for organization ${id}:`, err);
				throw err;
			}
		},
		[selectedOrganization, setSelectedOrganization],
	);

	return (
		<OrganizationContext.Provider
			value={{
				selectedOrganization,
				setSelectedOrganization,
				organizations,
				isLoading,
				error,
				fetchOrganizations,
				fetchOrganizationById,
				createOrganization,
				updateOrganization,
				uploadLogo,
			}}>
			{children}
		</OrganizationContext.Provider>
	);
};

export const useOrganization = () => {
	const context = useContext(OrganizationContext);
	if (context === undefined) {
		throw new Error('useOrganization must be used within an OrganizationProvider');
	}
	return context;
};

export default OrganizationContext;
